/**
 * HolidaysStrip — Faixa horizontal de datas comemorativas próximas.
 *
 * Aparece no topo da Agenda. Cada card mostra emoji, nome, dias restantes
 * e (ao clicar) abre um popover com sugestões de campanha específicas do
 * nicho do tenant + botão "Criar campanha WhatsApp".
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Calendar, Sparkles, ArrowRight, Plus, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import CampaignScheduleDialog from "@/components/CampaignScheduleDialog";

const formatBR = (iso) => {
  try {
    const d = new Date(iso + "T12:00:00");
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
  } catch { return iso; }
};

const relevanceStyle = {
  high: "border-tenant/40 bg-tenant/5",
  medium: "border-amber-500/30 bg-amber-500/5",
  low: "border-border bg-muted/30",
};

export default function HolidaysStrip() {
  const nav = useNavigate();
  const { slug } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [campaignFor, setCampaignFor] = useState(null); // holiday sendo agendada
  const [customOpen, setCustomOpen] = useState(false);

  const reload = () => {
    setLoading(true);
    return api.get("/tenant/agenda/holidays", { params: { days: 60 } })
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  const removeCustom = async (h) => {
    if (!h.custom_id) return;
    if (!window.confirm(`Remover a data "${h.name}"?`)) return;
    try {
      await api.delete(`/tenant/custom-holidays/${h.custom_id}`);
      toast.success("Data removida");
      await reload();
    } catch (e) { toast.error(formatApiError(e, "Erro ao remover")); }
  };

  if (loading) return null;

  // Mostra as 6 próximas datas
  const upcoming = items.slice(0, 6);

  return (
    <Card className="p-3 rounded-tenant border-border" data-testid="holidays-strip">
      <div className="flex items-center gap-1.5 mb-2">
        <Calendar className="w-3.5 h-3.5 text-tenant" />
        <div className="text-xs uppercase tracking-wide font-semibold">
          Datas comemorativas
        </div>
        <div className="text-[10px] text-muted-foreground">
          próximos 60 dias
        </div>
        <Button
          size="sm" variant="ghost"
          className="ml-auto h-7 text-[11px] gap-1"
          onClick={() => setCustomOpen(true)}
          data-testid="holidays-add-custom-btn"
        >
          <Plus className="w-3 h-3" /> Data personalizada
        </Button>
      </div>
      {upcoming.length === 0 ? (
        <div className="text-xs text-muted-foreground py-2">
          Nada nos próximos 60 dias. Adicione uma data personalizada.
        </div>
      ) : (
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="holidays-strip-list">
        {upcoming.map((h) => (
          <Popover key={`${h.name}-${h.date}`}>
            <PopoverTrigger asChild>
              <button
                data-testid={`holiday-chip-${h.date}`}
                className={`shrink-0 min-w-[130px] max-w-[180px] rounded-lg border p-2 text-left transition-colors hover:shadow-sm ${relevanceStyle[h.relevance] || relevanceStyle.low}`}
              >
                <div className="flex items-center gap-1 text-lg leading-none">
                  <span>{h.emoji}</span>
                  {h.custom && (
                    <Badge variant="outline" className="ml-auto text-[9px] px-1.5 py-0">
                      custom
                    </Badge>
                  )}
                  {!h.custom && h.days_to <= 7 && h.relevance !== "low" && (
                    <Badge variant="outline" className="ml-auto text-[9px] px-1.5 py-0 bg-tenant/10 text-tenant border-tenant/30">
                      urgente
                    </Badge>
                  )}
                </div>
                <div className="mt-1.5 text-xs font-semibold leading-tight line-clamp-2">
                  {h.name}
                </div>
                <div className="mt-0.5 text-[10px] text-muted-foreground">
                  {formatBR(h.date)}
                  {h.days_to === 0
                    ? " · hoje"
                    : h.days_to === 1
                    ? " · amanhã"
                    : ` · em ${h.days_to}d`}
                </div>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-3" side="bottom" align="start">
              <div className="flex items-start gap-2 mb-2">
                <span className="text-2xl leading-none">{h.emoji}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm">{h.name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {formatBR(h.date)}
                    {h.days_to === 0
                      ? " · é hoje!"
                      : ` · em ${h.days_to} dia${h.days_to > 1 ? "s" : ""}`}
                  </div>
                </div>
              </div>

              {(h.ideas || []).length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
                    <Sparkles className="w-3 h-3" /> Ideias pro seu negócio
                  </div>
                  <ul className="text-xs space-y-1">
                    {h.ideas.map((idea, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <span className="text-tenant">•</span>
                        <span>{idea}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-3 pt-2 border-t border-border flex gap-1.5">
                <Button
                  size="sm"
                  className="flex-1 h-7 text-xs gap-1"
                  onClick={() => setCampaignFor(h)}
                  data-testid={`holiday-campaign-${h.date}`}
                  style={{ background: "hsl(var(--tenant-primary))", color: "hsl(var(--tenant-primary-foreground))" }}
                >
                  Criar campanha <ArrowRight className="w-3 h-3" />
                </Button>
                {h.custom && (
                  <Button size="sm" variant="ghost" className="h-7 px-2"
                          onClick={() => removeCustom(h)}
                          data-testid={`holiday-remove-${h.custom_id}`}>
                    <Trash2 className="w-3 h-3 text-red-600" />
                  </Button>
                )}
              </div>
            </PopoverContent>
          </Popover>
        ))}
      </div>
      )}
      <CampaignScheduleDialog
        open={!!campaignFor}
        onOpenChange={(v) => { if (!v) setCampaignFor(null); }}
        holiday={campaignFor}
        onScheduled={reload}
      />
      <CustomHolidayDialog
        open={customOpen}
        onOpenChange={setCustomOpen}
        onSaved={reload}
      />
    </Card>
  );
}

function CustomHolidayDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ name: "", date: "", emoji: "🎉", is_recurring: true });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm({ name: "", date: "", emoji: "🎉", is_recurring: true });
  }, [open]);

  const submit = async () => {
    if (!form.name.trim() || !form.date) {
      toast.error("Preencha nome e data");
      return;
    }
    setBusy(true);
    try {
      await api.post("/tenant/custom-holidays", form);
      toast.success("Data personalizada adicionada");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e, "Erro ao salvar")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="custom-holiday-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Nova data personalizada</DialogTitle>
          <DialogDescription>Ex: aniversário do salão, black friday, campanha própria.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Nome</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   data-testid="custom-holiday-name" placeholder="Ex: Aniversário do salão" />
          </div>
          <div className="grid grid-cols-[1fr_100px] gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Data</Label>
              <Input type="date" value={form.date}
                     onChange={(e) => setForm({ ...form, date: e.target.value })}
                     data-testid="custom-holiday-date" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Emoji</Label>
              <Input value={form.emoji}
                     onChange={(e) => setForm({ ...form, emoji: e.target.value.slice(0, 4) })}
                     data-testid="custom-holiday-emoji" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_recurring}
                   onChange={(e) => setForm({ ...form, is_recurring: e.target.checked })}
                   data-testid="custom-holiday-recurring" />
            Repete todo ano
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} data-testid="custom-holiday-save"
                  style={{ background: "hsl(var(--tenant-primary))", color: "hsl(var(--tenant-primary-foreground))" }}>
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
